import { isValidElement, type ReactNode } from 'react';

/**
 * Marks a card that must occupy the FULL message width rather than sit inside a
 * chat bubble.
 *
 * FOR AGENT CARDS ONLY. `RainChatView` still names the generic tool card
 * directly (`type === RainToolCallCard`) -- that one lives in the core beside the
 * view, so naming it costs nothing.
 *
 * AN AGENT'S CARD IS DIFFERENT, AND THE COST IS MEASURABLE. `RainChatView` is
 * imported by roughly a dozen surfaces -- the quill editor chat, the automation
 * editor, the run page, the guidance overlay, the investigator, the agent
 * builder, the Exception Workbench. Hermes' `GraphEvidenceCard` is reachable by
 * exactly one path today (`useHermesRainAdapter` -> `HERMES_AGENT_CONFIG`), and
 * it drags ~2,000 lines of graph layout plus a 364-line stylesheet behind it.
 * Naming it here would make every one of those surfaces carry that, including
 * the ones that never draw a graph.
 *
 * The dependency direction says the same thing for its own reasons -- profiles
 * depend on the core, never the reverse, and a profile may reach into a feature
 * module the core must not touch (see RAIN_CORE_IMPORT_BAN in eslint.config.js).
 *
 * So the core publishes a marker and asks instead of importing. A card opts in by
 * assigning the flag on its component -- the same trick React itself uses for
 * `displayName` -- and the view checks for it structurally.
 */
export const RAIN_FULL_WIDTH_CARD = 'rainFullWidthCard' as const;

/** Shape of a component carrying the marker. */
export type TRainFullWidthCard = {
  [RAIN_FULL_WIDTH_CARD]?: true;
};

/** True when this node is an element whose component opted into full width. */
export function isFullWidthCardElement(node: ReactNode): boolean {
  return (
    isValidElement(node) &&
    (node.type as TRainFullWidthCard | undefined)?.[RAIN_FULL_WIDTH_CARD] ===
      true
  );
}
